/**
 * Tiny i18n shim — the AfriAnnotate frontend's first localization
 * layer. Drop-in API-compatible with `react-i18next`'s `useTranslation`
 * so we can swap in the real library later without rewriting call
 * sites.
 *
 * **Why a shim, not `react-i18next`:** the grant deliverable asked
 * for "i18n in 5 African languages" and we needed to ship the
 * scaffolding *plus* a working language switcher in one session.
 * Adding `react-i18next` (and its dep tree: `i18next`,
 * `i18next-browser-languagedetector`, etc.) would have eaten the
 * session on a `yarn install`. The shim:
 *   - flat JSON catalogues per language
 *   - `t("nav.projects")` lookup with optional `{{var}}` interpolation
 *   - language stored on the user model + persisted to localStorage
 *   - native `<Trans>` no-op for compatibility with future markup
 * Reads identical at the call site to react-i18next, so the future
 * upgrade is just a 5-line swap of this module. No string changes.
 *
 * **Five languages shipped:** English, Yorùbá, Hausa, Igbo, Swahili,
 * Amharic. The Amharic catalogue uses Ge'ez script; LTR enforced
 * (CSS already neutral on direction).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./locales/en.json";
import yo from "./locales/yo.json";
import ha from "./locales/ha.json";
import ig from "./locales/ig.json";
import sw from "./locales/sw.json";
import am from "./locales/am.json";

// Widened to string: the built-ins below plus any language published at
// runtime from a localization project (served by /api/locales/).
export type LangCode = string;

const CATALOGS: Record<string, Record<string, any>> = {
  en,
  yo,
  ha,
  ig,
  sw,
  am,
};

// Languages that ship in the build. ``SUPPORTED_LANGUAGES`` stays the
// static built-in list for back-compat with existing importers; call
// ``useAvailableLanguages()`` where the runtime-published ones should also
// appear (e.g. the language switcher).
export const SUPPORTED_LANGUAGES: { code: LangCode; name: string; endonym: string }[] = [
  { code: "en", name: en.language.name, endonym: en.language.endonym },
  { code: "yo", name: yo.language.name, endonym: yo.language.endonym },
  { code: "ha", name: ha.language.name, endonym: ha.language.endonym },
  { code: "ig", name: ig.language.name, endonym: ig.language.endonym },
  { code: "sw", name: sw.language.name, endonym: sw.language.endonym },
  { code: "am", name: am.language.name, endonym: am.language.endonym },
];

// ── Runtime-published locales ────────────────────────────────────────
// Translations published from a localization project are served at
// /api/locales/ and merged here at startup, so a new or updated language
// goes live WITHOUT a rebuild. Merge rule (in ``lookup``): a runtime string
// overrides the built-in for that key; built-ins fill any keys the runtime
// locale hasn't translated yet; English is the final fallback. So a partial
// published locale is safe — untranslated keys never go blank.
const RUNTIME_CATALOGS: Record<string, Record<string, any>> = {};
let RUNTIME_LANGUAGES: { code: string; name: string; endonym: string }[] = [];
const runtimeListeners = new Set<() => void>();
let runtimeLoadStarted = false;

/** Built-in languages plus any runtime-published ones not already built in. */
const availableLanguages = (): { code: string; name: string; endonym: string }[] => {
  const builtinCodes = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));
  return [...SUPPORTED_LANGUAGES, ...RUNTIME_LANGUAGES.filter((l) => !builtinCodes.has(l.code))];
};

/** Fetch published locales once and merge them into the runtime registry.
 *  Best-effort: if the endpoint is unreachable (offline, older backend),
 *  we silently keep the built-in catalogues. */
async function loadRuntimeLocales(): Promise<void> {
  if (runtimeLoadStarted || typeof window === "undefined" || typeof fetch === "undefined") return;
  runtimeLoadStarted = true;
  try {
    const manifest = await fetch("/api/locales/", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
    if (!Array.isArray(manifest) || manifest.length === 0) return;
    RUNTIME_LANGUAGES = manifest
      .filter((m) => m && typeof m.code === "string")
      .map((m) => ({ code: m.code, name: m.name || m.code, endonym: m.endonym || m.name || m.code }));
    await Promise.all(
      RUNTIME_LANGUAGES.map(async ({ code }) => {
        try {
          const bundle = await fetch(`/api/locales/${code}/`, { credentials: "same-origin" }).then((r) =>
            r.ok ? r.json() : null,
          );
          if (bundle && bundle.data && typeof bundle.data === "object") RUNTIME_CATALOGS[code] = bundle.data;
        } catch {
          /* skip this one, keep the rest */
        }
      }),
    );
    runtimeListeners.forEach((fn) => fn());
  } catch {
    /* keep built-ins */
  }
}

const LS_KEY = "afri:lang";
const DEFAULT_LANG: LangCode = "en";

const isSupported = (s: any): s is LangCode => typeof s === "string" && availableLanguages().some((l) => l.code === s);

/** Walk a single catalogue with a dotted key; ``undefined`` when absent. */
const walk = (catalog: any, key: string): string | undefined => {
  if (catalog == null) return undefined;
  let node: any = catalog;
  for (const p of key.split(".")) {
    if (node == null) return undefined;
    node = node[p];
  }
  return typeof node === "string" ? node : undefined;
};

/** Look up a key for a language: runtime-published string first, then the
 *  built-in catalogue, else ``undefined`` so callers fall back to English
 *  (or the literal key) — the ``fallbackLng: "en"`` contract. */
const lookup = (lang: LangCode, key: string): string | undefined => {
  const fromRuntime = walk(RUNTIME_CATALOGS[lang], key);
  if (fromRuntime !== undefined) return fromRuntime;
  return walk(CATALOGS[lang], key);
};

/** Mustache-lite interpolation: ``{{name}}`` substitution against
 *  the supplied params bag. Mirrors react-i18next's default. */
const interpolate = (template: string, params?: Record<string, string | number>) => {
  if (!params) return template;
  return template.replace(/\{\{\s*([^}\s]+)\s*\}\}/g, (_, k) => {
    const v = params[k];
    return v == null ? `{{${k}}}` : String(v);
  });
};

type Ctx = {
  lang: LangCode;
  setLang: (l: LangCode) => void;
  t: (key: string, params?: Record<string, string | number>, fallback?: string) => string;
};

/** Translate outside any provider: the persisted language, then English,
 *  then the explicit fallback, then the key. Needed because the labeling
 *  editor mounts in its own React root, so shared components rendered there
 *  (the Select's "Selected items" group, for one) never see the provider. */
const translateStandalone = (key: string, params?: Record<string, string | number>, fallback?: string) => {
  let lang: string = DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(LS_KEY);
    if (stored && isSupported(stored)) lang = stored;
  } catch {
    /* storage disabled */
  }
  const hit = lookup(lang as LangCode, key) ?? lookup("en", key);
  if (hit !== undefined) return interpolate(hit, params);
  if (fallback != null) return interpolate(fallback, params);
  return key;
};

const LocaleContext = createContext<Ctx>({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: translateStandalone,
});

/** Resolve initial language from (in order): persisted localStorage,
 *  user-profile preference (passed via ``initialLang``), the browser's
 *  ``navigator.language`` if it matches a supported code, then
 *  English. Keeps the user's choice sticky across reloads even
 *  before the user record loads from the API. */
const resolveInitial = (initialLang?: string | null): LangCode => {
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(LS_KEY);
      if (isSupported(stored)) return stored;
    } catch {
      /* storage disabled */
    }
  }
  if (isSupported(initialLang)) return initialLang;
  if (typeof navigator !== "undefined") {
    const nav = (navigator.language || "").slice(0, 2).toLowerCase();
    if (isSupported(nav)) return nav as LangCode;
  }
  return DEFAULT_LANG;
};

export const LocaleProvider = ({
  initialLang,
  children,
}: {
  initialLang?: string | null;
  children: React.ReactNode;
}) => {
  const [lang, setLangState] = useState<LangCode>(() => resolveInitial(initialLang));
  // Bumped when runtime-published locales finish loading, so ``t`` re-runs
  // with the newly-available strings.
  const [rtVersion, setRtVersion] = useState(0);

  // Load published locales once, then re-render. If the user had previously
  // picked a runtime-only language (persisted before it was loaded), adopt
  // it now that its catalogue is available.
  useEffect(() => {
    const onRuntime = () => {
      setRtVersion((n) => n + 1);
      try {
        const stored = window.localStorage.getItem(LS_KEY);
        if (stored && isSupported(stored)) {
          setLangState((cur) => (cur !== stored ? stored : cur));
        }
      } catch {
        /* storage disabled */
      }
    };
    runtimeListeners.add(onRuntime);
    loadRuntimeLocales();
    return () => {
      runtimeListeners.delete(onRuntime);
    };
  }, []);

  const setLang = useCallback((l: LangCode) => {
    if (!isSupported(l)) return;
    setLangState(l);
    try {
      window.localStorage.setItem(LS_KEY, l);
    } catch {
      /* storage disabled */
    }
    if (typeof document !== "undefined") document.documentElement.lang = l;
  }, []);

  // Sync initialLang prop changes (user-profile lookup completes
  // after first paint with a definitive language preference).
  useEffect(() => {
    if (initialLang && isSupported(initialLang) && initialLang !== lang) {
      // Only adopt the server's language if the user hasn't actively
      // chosen one in this session — localStorage wins for stickiness.
      try {
        if (window.localStorage.getItem(LS_KEY) === null) setLang(initialLang);
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLang]);

  // Set <html lang> on first mount so screen readers announce content
  // in the right language even before any string renders.
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  /** ``t(key, params?, fallback?)`` — returns the active-language
   *  string, falls back to English, then the explicit fallback,
   *  finally the literal key. Console-warns once per missing key
   *  (helps translators find gaps without spamming the page). */
  const t = useCallback<Ctx["t"]>(
    (key, params, fallback) => {
      const hit = lookup(lang, key);
      if (hit !== undefined) return interpolate(hit, params);
      const enHit = lookup("en", key);
      if (enHit !== undefined) return interpolate(enHit, params);
      if (fallback != null) return interpolate(fallback, params);
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing translation for key="${key}" (lang=${lang})`);
      }
      return key;
      // ``rtVersion`` in deps so ``t`` re-binds once runtime locales load.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [lang, rtVersion],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

/** Hook mirroring ``react-i18next``'s ``useTranslation()`` — returns
 *  ``{ t, i18n: { language, changeLanguage } }`` so existing
 *  react-i18next call patterns work unchanged after the upgrade. */
export const useTranslation = () => {
  const { lang, setLang, t } = useContext(LocaleContext);
  return {
    t,
    i18n: {
      language: lang,
      changeLanguage: (l: string) => {
        if (isSupported(l)) setLang(l);
      },
    },
  };
};

/**
 * Languages the switcher should offer: the built-in ones plus any
 * runtime-published locales (served by /api/locales/). Re-renders when the
 * runtime locales finish loading. Use this in the language picker in place
 * of the static ``SUPPORTED_LANGUAGES`` so published languages appear
 * without a rebuild.
 */
export const useAvailableLanguages = () => {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    runtimeListeners.add(fn);
    loadRuntimeLocales();
    return () => {
      runtimeListeners.delete(fn);
    };
  }, []);
  return availableLanguages();
};

/**
 * Transient locale override for a subtree.
 *
 * Wraps its children in a fresh ``LocaleContext`` whose ``lang`` is
 * ``override`` (when supported) or whatever the parent context already
 * resolves to. Any ``t()`` calls inside the subtree pick up the
 * override; everything outside it keeps the user's personal language.
 *
 * Designed for the per-project annotator-language split:
 * ``Project.annotator_language`` is fed in here on the labeling /
 * review routes, and management chrome stays untouched.
 *
 * The override is *display-only* — it does NOT touch localStorage and
 * does NOT call ``setLang``, so the operator's persisted preference is
 * preserved across the navigation. ``setLang`` from the inner context
 * still mutates the outer (real) one, which is intentional: a language
 * switch from the profile dropdown should always update the user's
 * personal preference, not the project override.
 */
export const LocaleScope = ({ override, children }: { override?: string | null; children: React.ReactNode }) => {
  const parent = useContext(LocaleContext);
  const value = useMemo(() => {
    if (!isSupported(override)) return parent;
    const lang = override as LangCode;
    const t = (key: string, params?: Record<string, string | number>, fallback?: string) => {
      const hit = lookup(lang, key);
      if (hit !== undefined) return interpolate(hit, params);
      const enHit = lookup("en", key);
      if (enHit !== undefined) return interpolate(enHit, params);
      if (fallback != null) return interpolate(fallback, params);
      return key;
    };
    return { lang, setLang: parent.setLang, t };
  }, [override, parent]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};
